/**
 * Photos d'un devis, telles que la planche A4 les reproduit.
 *
 * La planche reprend les photos jointes au devis entier, puis celles de chaque
 * ligne, légendées par la désignation de la ligne et leur nature — « Photo »
 * ou « Plan / croquis ». Un plan en PDF ne s'imprime pas sur une planche
 * d'images : il reste consultable depuis sa ligne.
 *
 * @param {object} devis devis tel que l'écran le tient
 * @returns {Array<{photo: string, libelle?: string, cat?: string}>} les images, dans l'ordre
 */
export function photosPlanche(devis) {
  const duDevis = (devis?.pieces || []).filter((x) => x && x.photo);
  const desLignes = (devis?.lignes || []).flatMap((l) =>
    (l.imgs || [])
      .filter((im) => im && im.data && !im.pdf)
      .map((im) => ({
        photo: im.data,
        libelle: l.d,
        cat: im.type === 'Plan' ? 'Plan / croquis' : 'Photo',
      })),
  );
  return [...duDevis, ...desLignes];
}

/**
 * La photo d'une ligne posée sur la ligne même du document — la
 * photo de l'article à la hauteur de l'article : la première photo de la
 * ligne, jamais un plan.
 *
 * @param {object} l ligne telle que l'écran la tient
 * @returns {string|undefined} l'image, ou rien
 */
export function photoDeLigne(l) {
  const im = (l?.imgs || []).find((x) => x && x.data && !x.pdf && x.type !== 'Plan');
  return im ? im.data : undefined;
}

/**
 * La première photo de chaque ligne se pose sur sa ligne : l'annexe
 * ne garde que les photos de l'offre entière, les plans et les photos en plus.
 *
 * @param {Array<{src: string, ligne?: string, nature?: string}>} photos images
 *   telles que le serveur les envoie, chacune avec la clé de sa ligne
 * @returns {{parLigne: Object<string, string>, lienParLigne: Object<string, string>,
 *   annexe: Array}} la photo de chaque ligne, par clé, l'adresse qui l'ouvre en
 *   grand, et le reste de l'annexe
 */
export function repartitPhotos(photos) {
  const parLigne = {};
  const lienParLigne = {};
  const annexe = [];
  for (const p of photos || []) {
    if (p && p.src && p.ligne && p.nature !== 'Plan / croquis' && !parLigne[p.ligne]) {
      parLigne[p.ligne] = p.src;
      // Touchée dans le PDF, la photo de l'article s'ouvre en grand aussi.
      if (p.lien) lienParLigne[p.ligne] = p.lien;
    } else {
      annexe.push(p);
    }
  }
  return { parLigne, lienParLigne, annexe };
}

/** Libellé que pose « 📷 Ajouter des photos » sur une photo de l'offre. */
export const LIBELLE_PHOTO = 'Photo';

/**
 * Une pièce jointe à l'offre est-elle une photo pour le client ?
 *
 * Les factures fournisseur jointes au même endroit portent le prix d'achat :
 * elles restent dans l'entreprise. La règle est celle du serveur
 * (`PieceJointeService.photosDuDevis`), qui les reprend dans le PDF envoyé.
 *
 * @param {object} piece pièce telle que l'API la rend
 * @returns {boolean} vrai pour une photo de l'offre entière
 */
export function estPhotoPourLeClient(piece) {
  return (
    !!piece &&
    !piece.ligneCle &&
    !!piece.fichier &&
    piece.libelle === LIBELLE_PHOTO &&
    /^image\/(jpeg|png|gif)$/.test(piece.typeMedia || '') &&
    !(piece.fournisseur || '').trim() &&
    !(piece.reference || '').trim() &&
    (piece.montant === null || piece.montant === undefined || piece.montant === '')
  );
}

/**
 * Photos de l'annexe du document A4 : celles de l'offre entière, puis celles
 * des lignes — l'ordre du PDF que le serveur envoie au client.
 *
 * @param {object} devis devis tel que l'écran le tient
 * @param {Array<object>} piecesServeur pièces de l'offre rendues par l'API
 * @param {(chemin: string) => string} adresse adresse d'un fichier de l'API
 * @param {{dansLesLignes?: boolean}} [options] la première photo
 *   de chaque ligne reste sur sa ligne, hors de l'annexe
 * @returns {Array<{src: string, legende: string, nature: string}>} les images
 */
export function photosAnnexe(devis, piecesServeur, adresse, { dansLesLignes = false } = {}) {
  const deLOffre = (piecesServeur || [])
    .filter(estPhotoPourLeClient)
    .map((p) => ({ src: adresse(p.fichier), legende: '', nature: 'Photo' }));
  // La photo de l'article est déjà sur sa ligne.
  const lignes = dansLesLignes
    ? (devis?.lignes || []).map((l) => {
        const pose = photoDeLigne(l);
        return pose ? { ...l, imgs: (l.imgs || []).filter((im) => im.data !== pose) } : l;
      })
    : devis?.lignes;
  const desLignes = photosPlanche({ lignes }).map((p) => ({
    src: p.photo,
    legende: p.libelle || '',
    nature: p.cat || 'Photo',
  }));
  return [...deLOffre, ...desLignes];
}

/** La note d'une photo, sans le nom de fichier que l'appareil lui a donné. */
function noteDe(libelle) {
  const l = (libelle || '').trim();
  if (!l || l === LIBELLE_PHOTO || /\.(jpe?g|png|gif|heic|heif|webp)$/i.test(l)) return '';
  return l;
}

/**
 * Photos jointes à une facture — les travaux réalisés — pour l'annexe du
 * document : la règle du serveur (`PieceJointeService.photosDeLaFacture`),
 * qui les reprend dans le PDF envoyé au client.
 *
 * @param {Array<object>} piecesServeur pièces de la facture rendues par l'API
 * @param {(chemin: string) => string} adresse adresse d'un fichier de l'API
 * @returns {Array<{src: string, legende: string, nature: string}>} les images
 */
export function photosFacture(piecesServeur, adresse) {
  return (piecesServeur || [])
    .filter(
      (p) =>
        p &&
        !p.ligneCle &&
        !!p.fichier &&
        /^image\/(jpeg|png|gif)$/.test(p.typeMedia || '') &&
        !(p.fournisseur || '').trim() &&
        !(p.reference || '').trim() &&
        (p.montant === null || p.montant === undefined || p.montant === ''),
    )
    .map((p) => ({ src: adresse(p.fichier), legende: noteDe(p.libelle), nature: 'Photo' }));
}
